import React, { FC, useCallback, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { message } from 'antd';
import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getBuyerSpecificAssetIdOrElseAssetId } from '../../utils/assetUtils';
import { readAssetsByIds, resolveAssetIdentifiers } from '../../thunks/assets_thunks';
import { PM_BULK_ASSET_UPLOAD_TARGET_COLLECTION } from '../../reducers/assets_reducer';
import ImportGridModal from '../ow_import_grid/ImportGridModal';
import { ImportField } from '../ow_import_grid/ImportGridTypes';

const ASSET_ID_FIELD_KEY = 'assetId';
const ASSET_NAME_FIELD_KEY = 'assetName';

// Template rows for the nothing-selected case. Intentionally invented rather than pulled from the
// account: `buyer_specific_asset_id` is sparsely populated, so real assets that happen to lack one
// would present bare numeric ids as though that were the company's format.
const FALLBACK_SAMPLE_ASSET_IDS = ['A-1024', '58311'];

// One free-text column, deliberately. "Asset ID" is what the app already shows for an asset
// (`AssetIdDisplay` renders `buyerSpecificAssetId || id`), so a customer can paste the column
// straight out of a report without knowing which of the two forms their assets carry. Asset
// *numbers* are not accepted: they are unique only per location, so they cannot identify an asset
// on their own.
const BULK_ASSET_ID_FIELDS: ImportField[] = [
	{
		label: 'Asset ID',
		key: ASSET_ID_FIELD_KEY,
		type: 'string',
		alternates: ['Asset Id', 'ID', 'Id', 'AssetID'],
		constraints: [{ type: 'required' }, { type: 'unique' }],
	},
	{
		// Display only — filled from the lookup, never from the uploaded file, so the user can see
		// WHICH asset each id resolved to before committing. `readonly` makes the grid render it
		// uneditable and keeps it out of paste/fill-down targets.
		label: 'Asset Name',
		key: ASSET_NAME_FIELD_KEY,
		type: 'string',
		readonly: true,
	},
];

const pluralizeAssets = (count: number): string => (count === 1 ? 'asset' : 'assets');

const normalizeIdentifier = (value: any): string =>
	value === undefined || value === null ? '' : String(value).trim().toLowerCase();

interface ResolvedAsset {
	assetId: number;
	name: string;
}

/**
 * Folds resolved assets into an identifier → asset map.
 *
 * Two passes on purpose. An identifier can legitimately be one asset's OpenWrench id and another
 * asset's buyer-specific id at the same time (both are just strings of digits), and the backend
 * resolves that collision in favour of the buyer-specific match. Writing every OW id first and then
 * letting the buyer-specific ids overwrite reproduces that precedence exactly; interleaving the two
 * per asset would make the winner depend on result order.
 */
const foldAssetsIntoMap = (assets: any[], target: Map<string, ResolvedAsset>): void => {
	const entryFor = (asset: any): ResolvedAsset => ({
		assetId: nullSafeGet('id', asset),
		name: nullSafeGetOrElse('name', asset, ''),
	});

	assets.forEach((asset) => {
		const assetId = nullSafeGet('id', asset);
		if (assetId !== undefined && assetId !== null) {
			target.set(normalizeIdentifier(assetId), entryFor(asset));
		}
	});

	assets.forEach((asset) => {
		const assetId = nullSafeGet('id', asset);
		const buyerSpecificAssetId = nullSafeGet('buyerSpecificAssetId', asset);
		if (buyerSpecificAssetId && assetId !== undefined && assetId !== null) {
			target.set(normalizeIdentifier(buyerSpecificAssetId), entryFor(asset));
		}
	});
};

// An asset the server returned that nobody asked for means the `buyerSpecificAssetIdOrIds` filter
// was not applied. An unrecognised filter key is not an error server-side — `AssetsFilterUtil` builds
// predicates only from keys it knows and the OpenSearch builder ends in `case _ => None` — so a
// server without the change answers with an UNFILTERED page of assets rather than a 400. The
// predicate is exactly `id IN (...) OR buyer_specific_asset_id IN (...)`, so a genuine no-match
// returns an EMPTY list, never unrelated rows. Detecting this matters twice over: it stops every row
// being marked "not found" when the real fault is the server, and it stops a coincidental id
// collision attaching the WRONG asset.
const hasStrayAssets = (assets: any[], requestedIdentifiers: string[]): boolean => {
	const requested = new Set(requestedIdentifiers.map(normalizeIdentifier));
	return assets.some(
		(asset) =>
			!requested.has(normalizeIdentifier(nullSafeGet('id', asset))) &&
			!requested.has(normalizeIdentifier(nullSafeGet('buyerSpecificAssetId', asset)))
	);
};

interface BulkAssetIdUploadModalProps {
	visible: boolean;
	onCancel: () => void;
	// Receives the OpenWrench asset ids that resolved and are not already on the schedule.
	onAssetsResolved: (assetIds: number[]) => void;
	// Everything that counts as "already there" — on the add-assets form that is the schedule's
	// scheduled assets PLUS anything staged. Uploaded ids matching these are skipped rather than
	// treated as an error, and the count feeds the "added to existing X" message.
	existingAssetIds?: number[];
	// Contents of the FIELD this modal writes into, which is not the same set as above: on the
	// add-assets form the upload feeds "Add Assets" while the scheduled assets live in a separate,
	// disabled "Existing Assets" field. The download has to mirror the field the button belongs to —
	// exporting the other field's contents from here would be nonsense.
	downloadableAssetIds?: number[];
	resolveIdentifiers: (identifiers: string[]) => Promise<any>;
	fetchAssetsByIds: (assetIds: number[]) => Promise<any>;
}

export const BulkAssetIdUploadModal: FC<BulkAssetIdUploadModalProps> = ({
	visible,
	onCancel,
	onAssetsResolved,
	existingAssetIds = [],
	downloadableAssetIds = [],
	resolveIdentifiers,
	fetchAssetsByIds,
}): React.ReactElement | null => {
	const [downloadingSample, setDownloadingSample] = useState(false);
	const hasDownloadableAssets = downloadableAssetIds.length > 0;
	// Identifiers from the most recent submit that matched no asset. The recordHook reads this
	// through a ref so the lookup always sees the freshest set without changing the hook's identity —
	// ImportGridModal re-runs validateRows with the hook after onDataLoad rejects, which is how these
	// land on the grid as per-cell errors.
	const unresolvedIdentifiersRef = useRef<Set<string>>(new Set());
	// Identifier → asset, accumulated across the post-mapping pre-check and submit. Submit only looks
	// up identifiers this does not already hold, so the common case (upload, glance at the names,
	// submit) costs ONE round trip over the file rather than two.
	const resolvedAssetsRef = useRef<Map<string, ResolvedAsset>>(new Map());
	// Set when the server ignored the lookup filter. Suppresses per-row "not found" errors, which
	// would otherwise blame the user's data for a deployment problem.
	const lookupUnavailableRef = useRef(false);

	const recordHook = useCallback((record: any) => {
		const rawIdentifier = record.get(ASSET_ID_FIELD_KEY);
		const identifier = normalizeIdentifier(rawIdentifier);

		// The identifier list travels to the backend comma-separated, so an embedded comma would be
		// read as two identifiers and match neither. Catch it here with a message that says why.
		if (identifier.indexOf(',') !== -1) {
			record.addError(ASSET_ID_FIELD_KEY, 'Asset ID cannot contain a comma.');
			return record;
		}

		if (
			identifier &&
			!lookupUnavailableRef.current &&
			unresolvedIdentifiersRef.current.has(identifier)
		) {
			record.addError(ASSET_ID_FIELD_KEY, 'No asset found with this Asset ID.');
		}

		return record;
	}, []);

	// Looks up whatever is not already cached and folds the results in. Returns the shared cache plus
	// whether the server's answer showed the filter was ignored.
	const resolveIntoCache = useCallback(
		(identifiers: string[]) => {
			const cache = resolvedAssetsRef.current;
			const unknownIdentifiers = Array.from(
				new Set(
					identifiers
						.map(normalizeIdentifier)
						.filter((identifier) => identifier.length > 0 && !cache.has(identifier))
				)
			);

			if (unknownIdentifiers.length === 0) {
				return Promise.resolve({ cache, lookupUnavailable: false });
			}

			return resolveIdentifiers(unknownIdentifiers).then((resolvedAssets) => {
				const assets = resolvedAssets || [];
				if (hasStrayAssets(assets, unknownIdentifiers)) {
					return { cache, lookupUnavailable: true };
				}
				foldAssetsIntoMap(assets, cache);
				return { cache, lookupUnavailable: false };
			});
		},
		[resolveIdentifiers]
	);

	const readIdentifiersFromRows = useCallback(
		(rows: Record<string, any>[]): string[] =>
			rows
				.map((row) => String(nullSafeGet(ASSET_ID_FIELD_KEY, row) || '').trim())
				.filter((identifier) => identifier.length > 0),
		[]
	);

	/**
	 * Post-mapping pre-check. Resolves the whole file up front so the user sees which rows are wrong —
	 * and the name of every asset that matched — before being asked to submit, rather than discovering
	 * it on rejection. Fills the readonly Asset Name column; errors reach the grid through recordHook,
	 * which ImportGridModal runs immediately after this.
	 */
	const resolveRows = useCallback(
		(rows: Record<string, any>[]) => {
			unresolvedIdentifiersRef.current = new Set();
			lookupUnavailableRef.current = false;

			const identifiers = readIdentifiersFromRows(rows);
			if (identifiers.length === 0) {
				return Promise.resolve(rows);
			}

			return resolveIntoCache(identifiers).then(({ cache, lookupUnavailable }) => {
				if (lookupUnavailable) {
					lookupUnavailableRef.current = true;
					message.error(
						'Asset ID lookup is not available on this server yet, so names could not be checked.'
					);
					return rows;
				}

				unresolvedIdentifiersRef.current = new Set(
					identifiers.map(normalizeIdentifier).filter((identifier) => !cache.has(identifier))
				);

				return rows.map((row) => {
					const resolved = cache.get(normalizeIdentifier(nullSafeGet(ASSET_ID_FIELD_KEY, row)));
					return { ...row, [ASSET_NAME_FIELD_KEY]: resolved ? resolved.name : '' };
				});
			});
		},
		[readIdentifiersFromRows, resolveIntoCache]
	);

	const onDataLoad = useCallback(
		(results) => {
			unresolvedIdentifiersRef.current = new Set();
			lookupUnavailableRef.current = false;

			const submittedIdentifiers = readIdentifiersFromRows(results.validData);

			if (submittedIdentifiers.length === 0) {
				return Promise.reject('Add at least one Asset ID.');
			}

			return resolveIntoCache(submittedIdentifiers).then(({ cache, lookupUnavailable }) => {
				if (lookupUnavailable) {
					lookupUnavailableRef.current = true;
					return Promise.reject(
						'Asset ID lookup is not available on this server yet, so the upload was stopped. ' +
							'This build needs the matching backend change (the buyerSpecificAssetIdOrIds ' +
							'asset filter). No assets were added.'
					);
				}

				const unresolvedIdentifiers = submittedIdentifiers.filter(
					(identifier) => !cache.has(normalizeIdentifier(identifier))
				);

				if (unresolvedIdentifiers.length > 0) {
					unresolvedIdentifiersRef.current = new Set(
						unresolvedIdentifiers.map(normalizeIdentifier)
					);
					return Promise.reject(
						`${unresolvedIdentifiers.length} of ${submittedIdentifiers.length} Asset IDs did not match an asset. Fix the highlighted rows and try again.`
					);
				}

				const existingAssetIdSet = new Set(existingAssetIds);
				const matchedAssetIds = submittedIdentifiers.map(
					(identifier) => cache.get(normalizeIdentifier(identifier)).assetId
				);
				// Two spreadsheet rows can name the same asset by its two different identifiers, so
				// de-duplicate on the resolved id rather than trusting the column's unique constraint.
				const assetIdsToAdd = Array.from(new Set(matchedAssetIds)).filter(
					(assetId) => !existingAssetIdSet.has(assetId)
				);
				const alreadyScheduledCount = new Set(matchedAssetIds).size - assetIdsToAdd.length;

				onAssetsResolved(assetIdsToAdd);

				const addedCount = assetIdsToAdd.length;
				const existingCount = existingAssetIds.length;
				// "added to existing X" only says something when there was an existing set; on a first
				// upload into an empty picker it would read as "added to existing 0 assets".
				const addedSentence =
					existingCount > 0
						? `${addedCount} ${pluralizeAssets(
								addedCount
						  )} added to existing ${existingCount} ${pluralizeAssets(existingCount)}.`
						: `${addedCount} ${pluralizeAssets(addedCount)} added.`;
				// Explains any gap between what was uploaded and what was added, so a smaller number
				// than expected never looks like rows were silently dropped.
				const alreadyAddedSentence =
					alreadyScheduledCount > 0 ? ` ${alreadyScheduledCount} skipped (already added).` : '';
				return `${addedSentence}${alreadyAddedSentence}`;
			});
		},
		[readIdentifiersFromRows, resolveIntoCache, existingAssetIds, onAssetsResolved]
	);

	const writeAssetIdCsv = useCallback((assetIds: string[], filename: string) => {
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename,
			headers: ['Asset ID'],
		});
		csvExporter.generateCsv(assetIds.map((assetId) => ({ assetId })));
	}, []);

	/**
	 * With assets already on the schedule, the useful download is the real list, not a template — it
	 * gives the user their current set in the exact format this column accepts, to extend in a
	 * spreadsheet and re-upload. It also sidesteps a trap in sampling arbitrary account assets:
	 * `buyer_specific_asset_id` is optional and often sparsely populated, so three assets that happen
	 * to lack one would show bare numeric ids and teach the wrong format with real-data authority.
	 * The current selection can't misrepresent itself.
	 *
	 * With nothing selected there is nothing to export, so this falls back to a static two-row
	 * template. It is deliberately NOT drawn from the account, for the sparse-population reason above;
	 * the modal text carries the "either form is accepted" explanation instead.
	 */
	const handleDownloadSample = useCallback(() => {
		if (!hasDownloadableAssets) {
			writeAssetIdCsv(FALLBACK_SAMPLE_ASSET_IDS, 'pm_schedule_asset_ids_sample');
			return;
		}

		setDownloadingSample(true);
		fetchAssetsByIds(downloadableAssetIds)
			.catch(() => [])
			.then((selectedAssets) => {
				const selectedAssetIds = (selectedAssets || [])
					.map(getBuyerSpecificAssetIdOrElseAssetId)
					.filter((assetId) => assetId !== undefined && assetId !== null && assetId !== '')
					.map(String);

				if (selectedAssetIds.length === 0) {
					// The lookup failed or came back empty; a template still beats an empty file.
					writeAssetIdCsv(FALLBACK_SAMPLE_ASSET_IDS, 'pm_schedule_asset_ids_sample');
					return;
				}
				writeAssetIdCsv(selectedAssetIds, 'pm_schedule_selected_asset_ids');
			})
			.finally(() => setDownloadingSample(false));
	}, [hasDownloadableAssets, fetchAssetsByIds, downloadableAssetIds, writeAssetIdCsv]);

	const handleCancel = useCallback(() => {
		unresolvedIdentifiersRef.current = new Set();
		resolvedAssetsRef.current = new Map();
		lookupUnavailableRef.current = false;
		onCancel();
	}, [onCancel]);

	if (!visible) {
		return null;
	}

	return (
		<ImportGridModal
			visible
			title="Upload Asset IDs"
			fields={BULK_ASSET_ID_FIELDS}
			recordHook={recordHook}
			onDataLoad={onDataLoad}
			onCancel={handleCancel}
			onComplete={handleCancel}
			resolveRows={resolveRows}
			resolvingRowsText="Looking up your Asset IDs..."
			onDownloadSample={handleDownloadSample}
			downloadingSample={downloadingSample}
			sampleModalText={
				hasDownloadableAssets
					? `Download the ${downloadableAssetIds.length} ${pluralizeAssets(
							downloadableAssetIds.length
					  )} already in this field to edit in a spreadsheet, or upload a new list. Uploading only adds — to remove an asset, clear it from the field instead.`
					: 'Upload a list of Asset IDs, or enter them manually. Use the Asset ID shown in OpenWrench — either your own asset ID or the OpenWrench ID is accepted.'
			}
			sampleButtonText={
				hasDownloadableAssets
					? `Download Selected (${downloadableAssetIds.length})`
					: 'Download Sample'
			}
		/>
	);
};

const mapDispatchToProps = (dispatch, ownProps: any) => ({
	resolveIdentifiers: (identifiers) =>
		dispatch(resolveAssetIdentifiers(ownProps.userType)(identifiers)),
	// `readAssetsByIds` splits the list across as many requests as the URL budget needs and derives
	// pageSize per chunk from its length, so a schedule with hundreds of assets exports all of them.
	fetchAssetsByIds: (ids) =>
		dispatch(readAssetsByIds(ownProps.userType)(ids, PM_BULK_ASSET_UPLOAD_TARGET_COLLECTION)),
});

export default connect(null, mapDispatchToProps)(BulkAssetIdUploadModal);
